import { isEqual, isEmpty } from 'lodash-es';
import { ref } from 'vue';
import { useSchemaStore } from '@/stores/schemaStore';
import { apiFetch } from '@/utils/api';
import { isProd, isMockData } from '@/utils/config';

/**
 * Generalized handler for POST/PATCH operations from a DataFormModal
 * @param {string} modelName - The name of the model being updated (e.g. sim/Sim)
 * @param {Object} updateData - The form data to be saved
 * @param {Object} [previousData] - The state of the object before editing 
 *  (for diffing)
 * @param {import('vue').Ref<Array<Object>>} [allRows] - The data set Vue Ref
 * @returns {Promise<Object>} The saved or updated entity
 */
export const handleRowSave = async (
  modelName,
  updateData,
  previousData = {},
  allRows = ref([]),
) => {
  // Pre-process data and model
  const schema = useSchemaStore();
  const model = schema.getFieldMeta(modelName);
  const endpoint = schema.getModelEndpoint(modelName);
  if (!model) throw new Error(`No model found for ${modelName}`);
  const primaryKey = model.find(field => field.primaryKey)?.name || 'id';
  const isEditing = !isEmpty(previousData);
  if (isEditing && isEqual(previousData, updateData)) {
    return updateData;
  }
  const optimizedUpdate = { ...updateData };
  // Handle PATCH diffing
  if (isEditing) {
    Object.keys(optimizedUpdate).forEach(key => {
      if (key !== primaryKey && optimizedUpdate[key] === previousData[key]) {
        delete optimizedUpdate[key];
      }
    });
  }
  // API Call
  let result;
  const method = isEditing ? 'PATCH' : 'POST';
  try {
    const results = await apiFetch(endpoint, {
      method: method,
      body: optimizedUpdate,
    });
    result = Array.isArray(results) ? results[0] : results;
  } catch (err) {
    console.error(`${method} ${endpoint} failed: ${err}`);
    if (isProd || !isMockData) {
      confirm(`Failed to ${isEditing ? 'update' : 'add'}: \n(${err.message})`);
      throw err;
    }
    console.warn("Dev Mode: Simulating successful update in local state");
    result = JSON.parse(JSON.stringify({ ...previousData, ...updateData }));
  }
  // Update Local State
  if (isEditing) {
    const index = allRows.value.findIndex(e => e[primaryKey] === result[primaryKey]);
    if (index !== -1) {
      Object.assign(allRows.value[index], result);
    }
  } else {
    allRows.value.push(result);
  }
  console.debug(`${isEditing ? 'Edit' : 'Add'} Success:`, JSON.stringify(result));
  return result;
};

/**
 * Generalized handler for DELETE operations from a DataFormModal
 * @param {string} modelName - The name of the model being deleted
 * @param {Object} deletionData - The entity being deleted
 * @param {Array<string>} [confirmProperties] - Optional list of properties to
 *  use for user confirmation context
 * @param {import('vue').Ref<Array<Object>>} [allRows] - The data set Vue Ref
 * @returns {Promise<boolean>} True if deleted
 */
export const handleRowDelete = async (
  modelName,
  deletionData,
  confirmProperties = [],
  allRows = ref([]),
) => {
  const schema = useSchemaStore();
  const model = schema.getFieldMeta(modelName);
  const endpoint = schema.getModelEndpoint(modelName);
  if (!model || !endpoint) throw new Error(`Invalid model or unknown endpoint`);
  let confirmed = false;
  const modelLabel = schema.getLabel(modelName);
  const pkField = model.find(field => field.primaryKey === true);
  const pkValue = deletionData[pkField.name];
  if (confirmProperties.length === 0) confirmProperties.push(pkField.label);
  const lock = !deletionData.isLocked;
  let confirmText = `${lock ? 'DELETE' : 'RESTORE'} ${modelLabel}:`;
  for (const prop of confirmProperties) {
    const field = model.find(f => f.label === prop || f.name === prop);
    confirmText += `\n\t${field.label}: ${deletionData[prop]}`;
  }
  if (confirm(`${confirmText}\n\nARE YOU SURE?`)) {
    try {
      let query = `?${pkField.name}=${pkValue}`;
      if (deletionData.isLocked) {
        console.debug(`Restoring deleted/locked resource`);
        query += `&lock=false`;
      }
      const response = await apiFetch(`${endpoint}${query}`, {
        method: 'DELETE',
      });
      if (response === true || response.isLocked === lock) {
        confirmed = true;
      } else {
        throw new Error(JSON.stringify(response));
      }
    } catch (err) {
      confirm(`Failed to delete ${modelLabel} (${err.message})`)
      console.error(`Failed to delete ${modelLabel} (${err.message})`);
      // TODO: check if return value has JSON body reason with details
      if (err.message === 'Not Found') {
        console.warn(`Removal assumes API call failed due to local test`);
        confirmed = true;
      }
    }
    if (confirmed && allRows.length) {
      allRows.splice(allRows.findIndex(device => device.id === detail.id), 1);
    }
  }
  return confirmed;
};
